import React, { useEffect, useMemo, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { fetchCourseCatalogCategories } from '../../utils/courseCatalogApi';

const CUSTOM = '__custom__';

/**
 * Select a canonical catalog category id or enter a custom legacy label.
 */
export default function CourseCatalogCategorySelect({ value, onChange, disabled }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const rows = await fetchCourseCatalogCategories();
        if (live) setOptions(Array.isArray(rows) ? rows : []);
      } catch {
        if (live) setOptions([]);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const presetIds = useMemo(() => new Set(options.map((o) => o.id)), [options]);
  // "Custom…" is a mode, not a value: picking it starts with an empty label, and
  // an empty label alone would read back as "— Not set —" and close the box.
  const [customMode, setCustomMode] = useState(false);

  const selectValue = useMemo(() => {
    const v = (value || '').trim();
    if (customMode) return CUSTOM;
    if (!v) return '';
    if (presetIds.has(v)) return v;
    return CUSTOM;
  }, [value, presetIds, customMode]);

  const handleSelect = (e) => {
    const v = e.target.value;
    if (v === CUSTOM) {
      setCustomMode(true);
      if (presetIds.has((value || '').trim())) onChange('');
      return;
    }
    setCustomMode(false);
    onChange(v);
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted small">
        <Spinner animation="border" size="sm" />
        Loading categories…
      </div>
    );
  }

  return (
    <>
      <Form.Select value={selectValue} onChange={handleSelect} disabled={disabled}>
        <option value="">— Not set —</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
        <option value={CUSTOM}>Custom…</option>
      </Form.Select>
      {selectValue === CUSTOM && (
        <Form.Control
          className="mt-2"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Custom category label (legacy / niche)"
          disabled={disabled}
        />
      )}
    </>
  );
}
